#!/usr/bin/env node
import './env-loader.js'
import { readFileSync } from 'node:fs'
import { fileURLToPath } from 'node:url'
import { dirname, join } from 'node:path'
import { Command } from 'commander'
// Dynamic version from package.json (works from both src/ and dist/)
const __dirname = dirname(fileURLToPath(import.meta.url))
const pkg = JSON.parse(readFileSync(join(__dirname, '..', 'package.json'), 'utf-8'))

const program = new Command()

program
  .name('nexus')
  .description('Nexus Backend CLI')
  .version(pkg.version)

// ── Info command ──────────────────────────────────────────────────────────────

program
  .command('info')
  .description('Show Nexus environment, database, plugins, and paths')
  .option('--json', 'Output as JSON')
  .action(async (options: { json?: boolean }) => {
    const { handleInfo } = await import('./cli/info-command.js')
    await handleInfo(options)
  })

// ── Migrate commands ────────────────────────────────────────────────────────

const migrate = program
  .command('migrate')
  .description('Database migration management')

migrate
  .command('create <name>')
  .description('Generate migration by comparing entities vs current schema (scope auto-detected from CWD)')
  .action(async (name: string) => {
    const { handleCreate } = await import('./cli/migrate-commands.js')
    await handleCreate(name)
  })

migrate
  .command('dev [name]')
  .description('Generate and apply migration automatically (scope auto-detected from CWD). Name defaults to "auto" if omitted')
  .action(async (name: string | undefined) => {
    const { handleDev } = await import('./cli/migrate-commands.js')
    await handleDev(name)
  })

migrate
  .command('up')
  .description('Run all pending migrations')
  .action(async () => {
    const { handleUp } = await import('./cli/migrate-commands.js')
    await handleUp()
  })

migrate
  .command('deploy')
  .description('Apply pending migrations (production-safe)')
  .option('--check-drift', 'Check for schema drift after migrations')
  .action(async (options: { checkDrift?: boolean }) => {
    const { handleDeploy } = await import('./cli/migrate-commands.js')
    await handleDeploy(options)
  })

migrate
  .command('down')
  .description('Rollback last batch of migrations (scope auto-detected from CWD, or specific migration with --name)')
  .option('--name <migration_name>', 'Rollback a specific migration by name')
  .action(async (options: { name?: string }) => {
    const { handleDown } = await import('./cli/migrate-commands.js')
    await handleDown(options)
  })

migrate
  .command('undo')
  .description('Rollback last migration AND delete its file (DEV ONLY, scope auto-detected from CWD)')
  .option('--force', 'Skip confirmation prompt')
  .action(async (options: { force?: boolean }) => {
    const { handleUndo } = await import('./cli/migrate-commands.js')
    await handleUndo(options)
  })

migrate
  .command('status')
  .description('Show migration status and schema drift')
  .action(async () => {
    const { handleStatus } = await import('./cli/migrate-commands.js')
    await handleStatus()
  })

migrate
  .command('list')
  .description('Show migration status (alias for "migrate status")')
  .action(async () => {
    const { handleStatus } = await import('./cli/migrate-commands.js')
    await handleStatus()
  })

migrate
  .command('reset')
  .description('Rollback all migrations and re-run (DEV ONLY, scope auto-detected from CWD)')
  .option('--force', 'Skip confirmation prompt')
  .action(async (options: { force?: boolean }) => {
    const { handleReset } = await import('./cli/migrate-commands.js')
    await handleReset(options)
  })

migrate
  .command('drop')
  .description('Rollback ALL batches of a source and remove records (DEV ONLY, scope auto-detected from CWD)')
  .option('--force', 'Skip confirmation prompt')
  .action(async (options: { force?: boolean }) => {
    const { handleDrop } = await import('./cli/migrate-commands.js')
    await handleDrop(options)
  })

// ── DB commands ──────────────────────────────────────────────────────────────

const db = program
  .command('db')
  .description('Direct database operations')

db
  .command('wipe')
  .description('DROP ALL TABLES in database (DANGEROUS)')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async (options: { yes?: boolean }) => {
    const { handleDbWipe } = await import('./cli/db-commands.js')
    await handleDbWipe(options)
  })

db
  .command('shell')
  .description('Open interactive database shell (psql, sqlite3, mysql)')
  .action(async () => {
    const { handleDbShell } = await import('./cli/db-commands.js')
    handleDbShell()
  })

db
  .command('info')
  .description('Show database connection, table count, and size')
  .action(async () => {
    const { handleDbInfo } = await import('./cli/db-commands.js')
    await handleDbInfo()
  })

// ── Plugin commands ─────────────────────────────────────────────────────────

const plugin = program
  .command('plugin')
  .description('Plugin management')

plugin
  .command('list')
  .description('List discovered plugins and their state')
  .option('--json', 'Output as JSON')
  .action(async (options: { json?: boolean }) => {
    const { handlePluginList } = await import('./cli/plugin-commands.js')
    await handlePluginList(options)
  })

plugin
  .command('add <name>')
  .description('Install a plugin (e.g. "charts" or "@gzl10/nexus-plugin-charts")')
  .option('-v, --version <version>', 'Specific version to install')
  .action(async (name: string, options: { version?: string }) => {
    const { handlePluginAdd } = await import('./cli/plugin-commands.js')
    await handlePluginAdd(name, options)
  })

plugin
  .command('remove [name]')
  .description('Uninstall a plugin (or all with -a)')
  .option('-a, --all', 'Remove all plugins')
  .action(async (name: string | undefined, options: { all?: boolean }) => {
    const { handlePluginRemove } = await import('./cli/plugin-commands.js')
    await handlePluginRemove(name, options)
  })

plugin
  .command('enable [name]')
  .description('Enable a disabled plugin (or all with -a)')
  .option('-a, --all', 'Enable all disabled plugins')
  .action(async (name: string | undefined, options: { all?: boolean }) => {
    const { handlePluginEnable } = await import('./cli/plugin-commands.js')
    await handlePluginEnable(name, options)
  })

plugin
  .command('disable [name]')
  .description('Disable a plugin without uninstalling (or all with -a)')
  .option('-a, --all', 'Disable all enabled plugins')
  .action(async (name: string | undefined, options: { all?: boolean }) => {
    const { handlePluginDisable } = await import('./cli/plugin-commands.js')
    await handlePluginDisable(name, options)
  })

plugin
  .command('info <name>')
  .description('Show plugin details (version, modules, entities, migrations)')
  .action(async (name: string) => {
    const { handlePluginInfo } = await import('./cli/plugin-commands.js')
    await handlePluginInfo(name)
  })

// ── Sync commands ───────────────────────────────────────────────────────────

program
  .command('sync:commands')
  .description('Sync Nexus Claude Code commands to ~/.claude/commands/')
  .option('--dry-run', 'Preview changes without applying')
  .action(async (options: { dryRun?: boolean }) => {
    const { handleSyncCommands } = await import('./cli/sync-commands.js')
    await handleSyncCommands(options)
  })

// ── Seed commands ───────────────────────────────────────────────────────────

const seed = program
  .command('seed')
  .description('Seed data management')

seed
  .command('export [entity]')
  .description('Export seedable entity data from DB to data/seeds/ JSONs')
  .action(async (entity?: string) => {
    const { handleSeedExport } = await import('./cli/seed-commands.js')
    await handleSeedExport(entity)
  })

// ── Stop command ──────────────────────────────────────────────────────────

program
  .command('stop')
  .description('Stop the running backend server')
  .option('-f, --force', 'Skip health/instance checks, kill by port directly')
  .action(async (options: { force?: boolean }) => {
    const { handleStop } = await import('./cli/stop-command.js')
    await handleStop(options)
  })

// ── Logs command ──────────────────────────────────────────────────────────

program
  .command('logs')
  .description('Tail backend log file in real time (requires LOG_FILE in .env)')
  .option('-n, --lines <lines>', 'Number of initial lines to show', '50')
  .option('-f, --file <path>', 'Log file path (default: LOG_FILE env or data/nexus.log)')
  .action(async (options: { lines?: string; file?: string }) => {
    const { handleLogs } = await import('./cli/logs-command.js')
    await handleLogs(options)
  })

// ── Client commands ────────────────────────────────────────────────────────

const client = program
  .command('client')
  .description('Connect to running backend via nexus-client (PAT auth, session in ~/.nexus/)')
  .option('-r, --remote', 'Connect via FRPC tunnel instead of localhost')

client
  .command('health')
  .description('Check backend health endpoint')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (options: { debug?: boolean }) => {
    const { handleClientHealth } = await import('./cli/client-commands.js')
    await handleClientHealth({ ...options, remote: client.opts()['remote'] })
  })

client
  .command('login [email] [password]')
  .description('Login and create a Personal Access Token (defaults to ADMIN from .env)')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (email: string | undefined, password: string | undefined, options: { debug?: boolean }) => {
    const { handleClientLogin } = await import('./cli/client-commands.js')
    await handleClientLogin(email, password, { ...options, remote: client.opts()['remote'] })
  })

client
  .command('me')
  .description('Get current authenticated user')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (options: { debug?: boolean }) => {
    const { handleClientMe } = await import('./cli/client-commands.js')
    await handleClientMe({ ...options, remote: client.opts()['remote'] })
  })

client
  .command('logout')
  .description('Revoke PAT and clear session')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (options: { debug?: boolean }) => {
    const { handleClientLogout } = await import('./cli/client-commands.js')
    await handleClientLogout({ ...options, remote: client.opts()['remote'] })
  })

client
  .command('fetch <method> <url> [body]')
  .description('Generic HTTP request: fetch GET /auth/me, fetch POST /mail/send \'{"to":"x"}\'')
  .option('-d, --debug', 'Enable debug logging')
  .option('--pat <token>', 'Use a PAT directly instead of session')
  .action(async (method: string, url: string, body: string | undefined, options: { debug?: boolean; pat?: string }) => {
    const { handleClientFetch } = await import('./cli/client-commands.js')
    const args = body ? [body] : []
    await handleClientFetch(method, url, args, { ...options, remote: client.opts()['remote'] })
  })

client
  .command('entity <path> <action> [args...]')
  .description('Entity CRUD: list, get <id>, create <json>, update <id> <json>, delete <id>')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (path: string, action: string, args: string[], options: { debug?: boolean }) => {
    const { handleClientEntity } = await import('./cli/client-commands.js')
    await handleClientEntity(path, action, args, { ...options, remote: client.opts()['remote'] })
  })

client
  .command('action <module> <action-key> [args...]')
  .description('Execute action: action <module> <key> [id] [json] (e.g., action auth sessions -m GET)')
  .option('-d, --debug', 'Enable debug logging')
  .option('-m, --method <method>', 'HTTP method (GET, POST, PUT, DELETE)', 'POST')
  .action(async (module: string, actionKey: string, args: string[], options: { debug?: boolean; method?: string }) => {
    const { handleClientAction } = await import('./cli/client-commands.js')
    await handleClientAction(module, actionKey, args, { ...options, remote: client.opts()['remote'] })
  })

// ── Client Storage commands ──────────────────────────────────────────────

const storage = client
  .command('storage')
  .description('Storage file operations (upload, download, list, delete, stats)')

storage
  .command('upload <files...>')
  .description('Upload one or more files to storage')
  .option('-d, --debug', 'Enable debug logging')
  .option('--folder <folder>', 'Target folder')
  .option('--scope <scope>', 'Storage config scope')
  .option('--visibility <visibility>', 'File visibility: public, internal, private')
  .action(async (files: string[], options: { debug?: boolean; folder?: string; scope?: string; visibility?: string }) => {
    const { handleStorageUpload } = await import('./cli/storage-commands.js')
    await handleStorageUpload(files, { ...options, remote: client.opts()['remote'] })
  })

storage
  .command('download <id>')
  .description('Download a file by ID (default: original filename in CWD)')
  .option('-d, --debug', 'Enable debug logging')
  .option('-o, --output <path>', 'Output file path')
  .action(async (id: string, options: { debug?: boolean; output?: string }) => {
    const { handleStorageDownload } = await import('./cli/storage-commands.js')
    await handleStorageDownload(id, { ...options, remote: client.opts()['remote'] })
  })

storage
  .command('list')
  .description('List storage files with optional filters')
  .option('-d, --debug', 'Enable debug logging')
  .option('--mimetype <mimetype>', 'Filter by MIME type')
  .option('--folder <folder>', 'Filter by folder')
  .option('--scope <scope>', 'Filter by scope')
  .option('-l, --limit <limit>', 'Max items to return')
  .action(async (options: { debug?: boolean; mimetype?: string; folder?: string; scope?: string; limit?: string }) => {
    const { handleStorageList } = await import('./cli/storage-commands.js')
    await handleStorageList({ ...options, remote: client.opts()['remote'] })
  })

storage
  .command('delete <id>')
  .description('Delete a file by ID')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (id: string, options: { debug?: boolean }) => {
    const { handleStorageDelete } = await import('./cli/storage-commands.js')
    await handleStorageDelete(id, { ...options, remote: client.opts()['remote'] })
  })

storage
  .command('stats')
  .description('Show storage statistics (total files, size, by type)')
  .option('-d, --debug', 'Enable debug logging')
  .action(async (options: { debug?: boolean }) => {
    const { handleStorageStats } = await import('./cli/storage-commands.js')
    await handleStorageStats({ ...options, remote: client.opts()['remote'] })
  })

program.parse()
